import React, {forwardRef, useEffect, useMemo, useRef, useState} from "react";
import styled from "styled-components";
import PlaceHolderView from "../../../Common/Views/PlaceHolderView";
import LogoIcon from "../../../Common/Images/LogoClear.png"
import Grid from "@material-ui/core/Grid";
import useCalendar from "../../../Common/Context/Consumers/useCalendar";
import CalendarType from "../../../Common/Models/CalendarType";
import {GroupedVirtuoso} from "react-virtuoso";
import CalendarEvent from "../../../Common/Models/CalendarEvent";
import {daySlots, timeSlots} from "../../../Common/Models/dateSlots";
import EventListItem from "../../../Common/Views/ListItems/EventListItem";
import CalendarListView from "./CalendarListView";
import CalendarGroupHeader from "./CalendarGroupHeader";
import useFavorites from "../../../Common/Context/Consumers/useFavorites";
import ItemType from "../../../Common/Models/ItemType";
import toggleFavorite from "../../../Common/Helper/toggleFavorite";
import {useAuthState} from "react-firebase-hooks/auth";
import Firebase from "firebase";
import triggerHaptics from "../../../Common/Helper/triggerHaptics";

interface CalendarBodyProps {
    page: number;
    onClick: (item: CalendarEvent) => void;
}

const BodyContainer = styled(Grid)`
  height: 100%;
`;

type DateGroup = {
   title: string;
   events: number;
};

const CalendarBody = (props: CalendarBodyProps) => {
    const {page, onClick} = props;
    const placeHolderText = ["No events today", "No events this week", "No events for the year", "No saved events"];
    const pages: CalendarType[]= [CalendarType.Today, CalendarType.Week, CalendarType.All, CalendarType.MyEvents];
    const currentCalendar = pages[page] === CalendarType.MyEvents ? CalendarType.All : pages[page]
    const events = useCalendar(currentCalendar);
    const filteredEvents = useRef(events);
    const [user] = useAuthState(Firebase.auth());
    const [dateGroups, setDateGroups] = useState<DateGroup[]>([]);
    const favorites: any = useFavorites();
    const Components = useMemo(() => {
        return {
            List: forwardRef(CalendarListView),
            Group: CalendarGroupHeader,
        };
    }, []);

    const onClickFavorite = (itemID: string, itemType: ItemType) => {
        toggleFavorite(itemID, itemType, favorites, user.uid);
        triggerHaptics();
    };


    const getItem = (index: number) => {
            let calendarEvent = filteredEvents.current[index];

            return calendarEvent ?
                <EventListItem
                    showDivider={true}
                    item={calendarEvent}
                    displayFavorite={!!(user)}
                    isFavorite={!!(favorites && favorites[ItemType.Event] && favorites[ItemType.Event][calendarEvent.id])}
                    onClickFavorite={onClickFavorite}
                    onClick={onClick}
                /> : <></>;
    };

    const createDateTitle = (date: Date, calendar: CalendarType): string => {
        let title;

        switch (calendar) {
            case CalendarType.Today:
                title = timeSlots[date.getHours()];
                break;
            case CalendarType.Week:
                title = daySlots[date.getDay()];
                break;
            case CalendarType.All:
            case CalendarType.MyEvents:
                title = date.toDateString();
                break;
        }

        return title;
    };

    const getGroups = (events: CalendarEvent[], calendar: CalendarType): DateGroup[] => {
        const groups: DateGroup[] = []
        let dateGroup: DateGroup | undefined = undefined;
        let previousDateTitle: string;
        let dateTitle;
        let date;

        events.forEach((calendarEvent) => {
            date = new Date(calendarEvent.startDate);
            dateTitle = createDateTitle(date, calendar);

            if (dateGroup && previousDateTitle && previousDateTitle !== dateTitle) {
                groups.push(dateGroup);
                dateGroup = {title: dateTitle, events: 0}
            }

            if (dateGroup === undefined)

                dateGroup = {title: dateTitle, events: 1}
            else
                dateGroup.events++;

            previousDateTitle = dateTitle;
        });

        if (dateGroup !== undefined)
            groups.push(dateGroup);

        return groups;
   };

    const getGroupCount = (dateGroups: DateGroup[]): number[] => {
        return dateGroups.map((dateGroup) => dateGroup.events);
    };

    const getSectionTitle = (index: number) => {
        const sectionTitle: string = dateGroups![index].title;
        return sectionTitle;
    };

    useEffect(() => {
        if (events ) {
            let calendarEvents;

            if (pages[page] === CalendarType.MyEvents) {
                calendarEvents = (favorites && favorites[ItemType.Event]) ?
                    events.filter((calendarEvent: CalendarEvent)=> favorites[ItemType.Event][calendarEvent.id]) : []
            } else {
                calendarEvents = events;
            }
            filteredEvents.current = calendarEvents;

            const groups = getGroups(calendarEvents, currentCalendar);

            setDateGroups(groups);
        }
    }, [favorites.Events, page]);

    return (
        <BodyContainer item xs={12} >
            {dateGroups && dateGroups.length > 0 ?
                < GroupedVirtuoso
                    style={{height: "100%", width: "auto"}}
                    overscan={200}
                    components={Components}
                    groupCounts={getGroupCount(dateGroups)}
                    groupContent={getSectionTitle}
                    itemContent={getItem}
                /> : <PlaceHolderView text={placeHolderText[page]} icon={LogoIcon} />
            }
        </BodyContainer>
    );
};

export default CalendarBody;